import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import * as $ from "jquery";
import "datatables.net";
import { Api } from "datatables.net";
import { Site } from "../site.models";

@Component({
  selector: "site-selector-table",
  templateUrl: "./site-selector-table.component.html",
  styleUrls: ["./site-selector-table.component.css"],
})
export class SiteSelectorTableComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() sites: Site[] = [];
  @Input() selectedSiteIds: number[] = [];
  @Input() pageLength = 5;
  @Input() searchable = true;
  @Input() ordering = true;
  @Input() loading = false;

  @Output() selectedSitesChange = new EventEmitter<Site[]>();
  @Output() selectedSiteIdsChange = new EventEmitter<number[]>();

  @ViewChild("siteSelectorTable", { static: false })
  tableRef!: ElementRef<HTMLTableElement>;

  private dataTable!: Api<any> | null;
  private selectedIds = new Set<number>();
  private initialized = false;

  ngAfterViewInit(): void {
    this.updateSelectedIdsFromInput();
    this.initDataTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["selectedSiteIds"] && !changes["selectedSiteIds"].firstChange) {
      this.updateSelectedIdsFromInput();
      if (this.dataTable) {
        this.dataTable.rows().invalidate("data").draw(false);
      }
    }

    if (changes["sites"] && !changes["sites"].firstChange) {
      const prev = (changes["sites"].previousValue ?? []) as Site[];
      const curr = (changes["sites"].currentValue ?? []) as Site[];
      if (this.sitesChanged(prev, curr)) {
        if (this.dataTable && this.initialized) {
          this.dataTable.clear();
          this.dataTable.rows.add(this.sites ?? []);
          this.dataTable.draw();
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.dataTable) {
      this.dataTable.destroy(true);
      this.dataTable = null;
    }
  }

  private initDataTable(): void {
    if (this.initialized || !this.tableRef?.nativeElement) {
      return;
    }

    const table = $(this.tableRef.nativeElement);
    this.dataTable = table.DataTable({
      data: this.sites ?? [],
      columns: [
        { data: null, orderable: false, defaultContent: "" },
        { data: "siteName", defaultContent: "-" },
        { data: "city", defaultContent: "-" },
        { data: "state", defaultContent: "-" },
      ],
      columnDefs: [
        {
          targets: 0,
          render: (row: Site) => {
            const siteId = row?.siteId;
            const isChecked =
              siteId != null && this.selectedIds.has(siteId) ? "checked" : "";
            return `<input type="checkbox" class="select-row" ${isChecked} />`;
          },
        },
      ],
      paging: true,
      pageLength: this.pageLength,
      searching: this.searchable,
      ordering: this.ordering,
      order: [],
      stripeClasses: [],
      info: false,
      language: {
        emptyTable: "No sites available.",
      },
    }) as unknown as Api<any>;

    $(this.tableRef.nativeElement).on("change", "input.select-row", (event) => {
      const rowElement = $(event.target).closest("tr");
      const rowData = this.dataTable?.row(rowElement).data() as Site;
      const siteId = rowData?.siteId;

      if (siteId == null) {
        return;
      }

      if ((event.target as HTMLInputElement).checked) {
        this.selectedIds.add(siteId);
      } else {
        this.selectedIds.delete(siteId);
      }

      this.emitSelection();
    });

    this.initialized = true;
  }

  private updateSelectedIdsFromInput(): void {
    const ids = (this.selectedSiteIds ?? []).filter(
      (id): id is number => typeof id === "number",
    );
    this.selectedIds = new Set<number>(ids);
  }

  private sitesChanged(prev: Site[], curr: Site[]): boolean {
    if (prev === curr) return false;
    if (prev.length !== curr.length) return true;
    for (let i = 0; i < prev.length; i++) {
      const prevId = prev[i]?.siteId;
      const currId = curr[i]?.siteId;
      if (prevId !== currId) return true;
    }
    return false;
  }

  private emitSelection(): void {
    const selectedSites = this.getSelectedSites();
    this.selectedSitesChange.emit(selectedSites);
    const selectedIds = selectedSites
      .map((site) => site.siteId)
      .filter((id): id is number => typeof id === "number");
    this.selectedSiteIdsChange.emit(selectedIds);
  }

  getSelectedSites(): Site[] {
    return (this.sites ?? []).filter(
      (site) => site?.siteId != null && this.selectedIds.has(site.siteId),
    );
  }

  getSelectedSiteIds(): number[] {
    return this.getSelectedSites()
      .map((site) => site.siteId)
      .filter((id): id is number => typeof id === "number");
  }
}
